import { useGetPlaybookQuery } from '@carboncopyco/data';
import { EditIcon, OpenInNew1OutlineIcon } from '@carboncopyco/icons';
import {
  type PlaybookAsset,
  isExternalUrlAssetType,
} from '@carboncopyco/types';
import {
  Badge,
  Button,
  TextBase,
  TextLargeParagraph,
  TooltipBubble,
  UserBasicInfo,
} from '@carboncopyco/ui';
import { MMMDoYYYY } from '@carboncopyco/utils';
import { useState } from 'react';
import { AssetTableSkeleton } from '../../../Dashboard/AssetTableSkeleton';
import { TableErrorState } from '../../../Dashboard/SessionBreakdownComponents';
import { FixedTable } from '../../../FixedTable';
import type { Col } from '../../../FixedTable';
import { UpdatePlaybookModal } from '../../../PlaybookModal';
import { useDesktopDeeplink } from '../../../utils';
import { TitleCard } from '../../InfoPageCards';
import { usePlaybookPageProps } from '../PlaybookPageContext';
import { AssetNameLink } from './AssetNameLink';
import { AssetsTableRowWrapper } from './AssetsTableRowWrapper';
import { AutoSyncInfoIcon } from './AutoSyncInfoIcon';

const cols: Col<PlaybookAsset>[] = [
  {
    alignment: 'left',
    title: 'Asset name',
    width: 400,
    render: (asset, index) => (
      <AssetNameLink
        asset={asset}
        index={index}
      />
    ),
  },
  {
    alignment: 'left',
    title: 'Created by',
    width: 200,
    render: ({ entity }) => (
      <UserBasicInfo
        user={entity?.creator}
        textColor="text-ds-natural-600"
        truncateWidth={130}
        tooltip
        size="small"
      />
    ),
  },
  {
    alignment: 'left',
    title: 'Last updated',
    width: 290,
    render: ({ entity, assetRevisionId }) => {
      if (isExternalUrlAssetType(entity)) {
        return null;
      }
      return (
        <TooltipBubble
          title={`${MMMDoYYYY(entity?.updateTime)} (Version ${assetRevisionId + 1})`}
          placement="top-start"
        >
          <TextBase
            label={`${MMMDoYYYY(entity.updateTime)} (Version ${assetRevisionId + 1})`}
          />
        </TooltipBubble>
      );
    },
  },
  {
    alignment: 'center',
    title: <AutoSyncInfoIcon />,
    width: 90,
    render: ({ entity, autoSync }) => {
      if (isExternalUrlAssetType(entity)) {
        return null;
      }
      return autoSync ? (
        <Badge
          round
          variant="green"
          label="On"
        />
      ) : (
        <Badge
          round
          variant="grey"
          label="Off"
        />
      );
    },
  },
];

export const AssetsTable = () => {
  const { playbookId, workspaceId, organizationId, readOnly } =
    usePlaybookPageProps();
  const {
    data: playbook,
    isLoading,
    error,
  } = useGetPlaybookQuery({
    id: playbookId,
    workspaceId,
    organizationId,
  });
  const { exeDeeplink, shouldShowDeeplink } = useDesktopDeeplink();
  const [openPlaybookModal, setOpenPlaybookModal] = useState(false);

  return (
    <>
      <TitleCard
        border="ds-regular"
        header={
          <div className="p-6 h-ds-15 flex items-center justify-between gap-2">
            <div className="flex gap-1">
              <TextLargeParagraph
                label="Assets"
                color="text-ds-natural-600"
              />
              {playbook?.assets && (
                <TextLargeParagraph
                  label={`(${playbook?.assets.length})`}
                  color="text-ds-natural-600"
                />
              )}
            </div>
            {!readOnly && (
              <Button
                blue
                link
                leftIcon={<EditIcon />}
                label="Update"
                onClick={() =>
                  exeDeeplink('update', playbookId, () =>
                    setOpenPlaybookModal(true)
                  )
                }
                rightIcon={
                  shouldShowDeeplink ? (
                    <OpenInNew1OutlineIcon size={15} />
                  ) : null
                }
              />
            )}
          </div>
        }
      >
        {error ? (
          <div className="p-28">
            <TableErrorState />
          </div>
        ) : (
          <div className="flex flex-col pt-4.5 h-full">
            <FixedTable<PlaybookAsset>
              sideSpacing
              cols={cols}
              data={playbook?.assets ?? []}
              isLoading={isLoading}
              loader={<AssetTableSkeleton />}
              rowWrapper={AssetsTableRowWrapper}
              loaderPlaceholderSpace={6}
            />
          </div>
        )}
      </TitleCard>
      {openPlaybookModal && (
        <UpdatePlaybookModal
          onClose={() => setOpenPlaybookModal(false)}
          id={playbookId}
        />
      )}
    </>
  );
};
